import Loading from "../../common/components/loading/loading";
import { withTranslation } from "react-i18next";
import Input from "../../common/components/form/input";
import Form from "../../common/components/form/form";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Joi from "joi-browser";
import {
  getSubmittedData,
  getLatestSettlementSubmissionTime,
  processData,
} from "../../common/services/marketdata/ngxSettlementService";
import { getExternalDataSourcesByMarketId } from "../../common/services/marketdata/externalDataSourceService";
import { getUrlsByExternalDataSourceId } from "../../common/services/marketdata/externalDataSourceUrlService";
import { formatDateTimeWithTimeZone } from "../../common/services/utilities";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import Select from "../../common/components/form/select";

class ExternalData extends Form {
  state = {
    data: {
      externalDataSourceId: null,
      marketDate: null,
      rawData: "",
      dateAdded: null,
    },
    errors: {},
    externalDataSources: [],
    externalDataSourceUrls: [],
    submittedData: [],
    isLoading: true,
    showInfoModal: false,
    showSubmittedDataModal: false,
    allowEdit: false,
    scrapedData: null,
    sortColumn: { field: "ngxMarket", sort: "asc" },
  };

  schema = {
    marketDate: Joi.date(),
    externalDataSourceId: Joi.number().required(),
    rawData: Joi.string().required(this.props.t("Data")),
    dateAdded: Joi.string().allow(null, ""),
  };

  async componentDidMount() {
    const { marketId } = this.props;

    const date = new Date();

    //TODO: Implement DATA-529 to make the default date dynamic
    date.setDate(date.getDate() - 1);
    const marketDate = date.toLocaleDateString("fr-CA");

    const externalDataSources = await getExternalDataSourcesByMarketId(
      marketId
    );

    const externalDataSourceId =
      externalDataSources.length > 0 ? externalDataSources[0].id : null;

    const externalDataSourceUrls =
      externalDataSourceId != null
        ? await this.getExternalSourceUrls(externalDataSourceId)
        : [];

    const dateAdded = await this.getLatestSubmissionTime(
      externalDataSourceId,
      marketDate
    );

    const submittedData = await this.fetchSubmittedData(
      externalDataSourceId,
      marketDate
    );

    this.setState({
      data: {
        ...this.state.data,
        marketDate: marketDate,
        externalDataSourceId: externalDataSourceId,
        dateAdded: dateAdded,
        rawData: "",
      },
      externalDataSources: externalDataSources,
      externalDataSourceUrls: externalDataSourceUrls,
      submittedData: submittedData,
      isLoading: false,
    });
  }

  getExternalSourceUrls = async (externalDataSourceId) =>
    await getUrlsByExternalDataSourceId(externalDataSourceId);

  handleMarketDateChange = async (marketDate) => {
    const { externalDataSourceId } = this.state.data;

    const dateAdded = await this.getLatestSubmissionTime(
      externalDataSourceId,
      marketDate
    );

    const submittedData = await this.fetchSubmittedData(
      externalDataSourceId,
      marketDate
    );

    this.setState({
      data: {
        ...this.state.data,
        marketDate,
        dateAdded,
      },
      submittedData,
    });
  };

  handleExternalDataSourceChange = async (id) => {
    const { marketDate } = this.state.data;

    const dateAdded = await this.getLatestSubmissionTime(id, marketDate);

    const externalDataSourceUrls = await this.getExternalSourceUrls(id);

    const submittedData = await this.fetchSubmittedData(id, marketDate);

    this.setState({
      data: {
        ...this.state.data,
        externalDataSourceId: id,
        dateAdded,
        rawData: "",
      },
      externalDataSourceUrls: externalDataSourceUrls,
      submittedData: submittedData,
    });
  };

  fetchSubmittedData = async (externalDataSourceId, marketDate) => {
    try {
      return await getSubmittedData(externalDataSourceId, marketDate);
    } catch (error) {
      console.error("Error fetching submitted data:", error);
    }
  };

  getLatestSubmissionTime = async (externalDataSourceId, marketDate) => {
    let dateAdded = await getLatestSettlementSubmissionTime(
      externalDataSourceId,
      marketDate
    );

    return dateAdded.data ?? null;
  };

  handlePaste = (event) => {
    const data = { ...this.state.data, rawData: event.target.value };
    this.setState({ data });
  };

  handleSubmit = (e) => {
    e.preventDefault(); // Prevent default form submission
    this.doSubmit(); // Call the doSubmit method you've already defined
  };

  handleInfoModalClose = () => {
    this.setState({ showInfoModal: false });
  };

  handleSubmittedDataModalClose = () => {
    this.setState({ showSubmittedDataModal: false });
  };

  doSubmit = async () => {
    try {
      const dateAdded = new Date().toISOString();

      await processData({ ...this.state.data, dateAdded });

      const { externalDataSourceId, marketDate } = this.state.data;
      const submittedData = await this.fetchSubmittedData(
        externalDataSourceId,
        marketDate
      );

      const updatedData = {
        ...this.state.data,
        dateAdded,
        rawData: "",
      };

      this.setState({ data: updatedData, submittedData });

      toast.success(
        this.props.t("SuccessfullySubmittedDataForN", {
          dataType: this.props.t("NgxSettlement"),
        })
      );
    } catch (ex) {
      console.log(
        this.props.t("FailedToSubmitDataForN", {
          dataType: this.props.t("NgxSettlement"),
        })
      );

      if (ex.response && ex.response.status === 400) {
        var errorList = ex.response.data.errors;
        if (errorList) {
          var errorKeys = Object.keys(ex.response.data.errors);
          errorKeys.forEach(function (key) {
            errorList[key].map((error) => {
              toast.error(
                this.props.t("FailedToSubmitDataForN", {
                  dataType: this.props.t("NgxSettlement"),
                }) +
                  " " +
                  key +
                  ": " +
                  error
              );
              return null;
            });
          });
        } else {
          toast.error(
            this.props.t("FailedToSubmitDataForN", {
              dataType: this.props.t("NgxSettlement"),
            }) +
              " " +
              ex.response.data
          );
        }

        toast.error(ex.response.data.errors.title);
      }
    }
  };

  // New method to handle sorting
  handleSort = (sortModel) => {
    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      this.setState({ sortColumn: { field: colId, sort } });
    }
  };

  // Updated AG Grid implementation
  renderSubmittedDataTable = (submittedData) => {
    // Define default column properties
    const defaultColDef = {
      sortable: true,
      resizable: true,
      filter: true,
    };

    // Define column definitions
    const columnDefs = [
      {
        field: "ngxMarket",
        headerName: "NGX Market",
        cellStyle: { textAlign: "center" },
      },
      {
        field: "beginDate",
        headerName: "Begin Date",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
      },
      {
        field: "endDate",
        headerName: "End Date",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
      },
      {
        field: "numberOfTrades",
        headerName: "Number of Trades",
        cellStyle: { textAlign: "right" },
      },
      {
        field: "totalVolume",
        headerName: "Total Volume",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) => params.value.toFixed(2),
      },
      {
        field: "openPrice",
        headerName: "Open Price",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) =>
          params.value !== null ? params.value.toFixed(4) : "N/A",
      },
      {
        field: "highPrice",
        headerName: "High Price",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) =>
          params.value !== null ? params.value.toFixed(4) : "N/A",
      },
      {
        field: "lowPrice",
        headerName: "Low Price",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) =>
          params.value !== null ? params.value.toFixed(4) : "N/A",
      },
      {
        field: "settlePrice",
        headerName: "Settle Price",
        cellStyle: { textAlign: "right" },
        valueFormatter: (params) =>
          params.value !== null ? params.value.toFixed(4) : "N/A",
      },
      {
        field: "netOpenInterest",
        headerName: "Net Open Interest",
        cellStyle: { textAlign: "right" },
      },
    ];

    return (
      <ThemeableAgGrid
        className="table table-dark table-striped table-hover"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={this.state.submittedData}
        onSortChanged={(event) => this.handleSort(event.api.getSortModel())}
        animateRows={true}
        domLayout="autoHeight"
        getRowId={(params) =>
          `${params.data.ngxMarket}-${params.data.beginDate}-${params.data.endDate}`
        }
      />
    );
  };

  render() {
    const {
      isLoading,
      data,
      scrapedData,
      showInfoModal,
      showSubmittedDataModal,
      externalDataSources,
      externalDataSourceUrls,
      submittedData,
    } = this.state;
    const { externalDataSourceId, marketDate } = this.state.data;
    const { t } = this.props;

    if (isLoading) return <Loading />;

    const endDate = new Date().toLocaleDateString("fr-CA");
    const externalDataSourceName = externalDataSources.find(
      (x) => x.id === externalDataSourceId
    )?.name;

    return (
      <div className="container-fluid">
        <Modal
          show={showInfoModal}
          size="md"
          onHide={this.handleInfoModalClose}
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <div className="flex-grow-1 text-center">
                <Modal.Title>
                  {t("SubmitExternalDataHelpModalTitle")}
                </Modal.Title>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col" style={{ textAlign: "center" }}>
                {t("NgxSettlementDataInformation1")}
                <table className="table table-bordered">
                  <br></br>
                  <tbody>
                    {externalDataSourceUrls.map((url, index) => (
                      <tr key={index}>
                        <td>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {url}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ whiteSpace: "pre-line" }}>
                  {t("NgxSettlementDataInformation2")}
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={showSubmittedDataModal}
          size="xl"
          onHide={() => this.setState({ showSubmittedDataModal: false })}
          backdrop="static"
          keyboard={false}
          dialogClassName="modal-90w"
        >
          <Modal.Header>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <div className="flex-grow-1 text-center">
                <Modal.Title>
                  {t("SubmittedDataModalTitle", {
                    externalDataSource: externalDataSourceName,
                    date: marketDate,
                  })}
                </Modal.Title>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: "calc(100vh - 210px)",
              overflowY: "auto",
            }}
          >
            {this.renderSubmittedDataTable(submittedData)}
          </Modal.Body>
        </Modal>

        <form onSubmit={this.handleSubmit}>
          <div className="row">
            <div className="row">
              <div className="col-md-2" style={{ paddingTop: "10px" }}>
                <Select
                  idField="id"
                  name="name"
                  disabled={Object.keys(externalDataSources).length < 2}
                  value={externalDataSourceId}
                  label={t("ExternalDataSource")}
                  options={externalDataSources}
                  onChange={(e) =>
                    this.handleExternalDataSourceChange(e.currentTarget.value)
                  }
                />
              </div>
              <div className="col-md-2" style={{ paddingTop: "10px" }}>
                <Input
                  type="date"
                  max={endDate}
                  onChange={(e) =>
                    this.handleMarketDateChange(e.currentTarget.value)
                  }
                  value={data.marketDate}
                  label={t("MarketDate")}
                />
              </div>
              <div className="col-md-8 mt-auto">
                <div className="text-end">{t("LastSubmittedDate")}</div>
                <div
                  className="text-end"
                  style={{
                    color: data.dateAdded ? "green" : "yellow",
                    cursor: data.dateAdded ? "pointer" : "default",
                  }}
                  onClick={(e) => {
                    if (data.dateAdded) {
                      e.preventDefault();
                      this.setState({ showSubmittedDataModal: true });
                    }
                  }}
                >
                  {data.dateAdded ? (
                    <>
                      {formatDateTimeWithTimeZone(
                        data.dateAdded,
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      )}
                      <i
                        className="fa-solid fa-up-right-from-square ms-2 fa-sm"
                        style={{ color: "white" }}
                      ></i>
                    </>
                  ) : (
                    t("NoSubmissionForThisDate")
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12" style={{ paddingTop: "15px" }}>
                {this.renderTextArea(
                  "rawData",
                  t("Data"),
                  t("NgxSettlementDataPlaceholder"),
                  <button
                    className="no-border no-background"
                    style={{
                      visibility:
                        externalDataSourceUrls.length > 0
                          ? "visible"
                          : "hidden",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      this.setState({ showInfoModal: true });
                    }}
                  >
                    <i className="fa fa-question-circle" aria-hidden="true"></i>
                  </button>
                )}
              </div>
              <div
                className="col-md-12 align-right"
                style={{ paddingTop: "15px" }}
              >
                {this.renderButton(t("Submit"), "submit")}
              </div>
            </div>
          </div>
        </form>
        <div className="col-md-10">
          {scrapedData && <pre>{JSON.stringify(scrapedData, null, 2)}</pre>}
        </div>
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(ExternalData);
